// ─── Impostazioni → Listino: listini con un titolo, e un foglio per tipologia ─
//
// 🗣️ Disegnato da lui il 17/09/2026, per esteso: *«ogni tabella è una tipologia
// — partita, lezione… metto una cifra in una casella, poi la trascino… basta
// scrivere la data di inizio e la data di fine, e se non si mette la data di
// fine non finisce mai… e la possibilità di dare un titolo a un listino»*.
//
// ⭐⭐ La cosa che questo schermo fa sparire è un MECCANISMO, non solo delle
// caselle: specificità, strati, dominanza, ambiguità e conflitti esistevano per
// arbitrare righe che si sovrappongono. Le caselle non si sovrappongono.
// 📌 *La cura migliore per una regola difficile non è scriverla meglio: è
//    togliere il caso che la rendeva necessaria.*

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GIORNI,
  ORARIO_CIRCOLO_PREDEFINITO,
  TIPI_TABELLA_GIOCATORI,
  listinoPerData,
  oreDellaGriglia,
  type Destinatari,
  type ListinoCompleto,
  type OrarioCircolo,
  type TabellaPrezzi,
  type TipoTabellaGiocatori,
} from '@pmo/shared';
import { Link, useSearchParams } from 'react-router-dom';
import { chiama } from '../../lib/api.js';
import { CampoData } from '../../components/CampoData.js';
import { inItaliano, oggi } from '../../lib/time.js';
import { Attesa, Campo, Guasto, Vuoto } from '../../components/pezzi.js';
import { Foglio, type DaScrivere } from '../../features/foglio/Foglio.js';
import { centesimi, prezzo, prezzoDelCampo } from '../../features/listini/prezzo.js';
import { SoloLettura, euro, usaModifica } from './comune.js';

const CHIAVE = 'listini';

interface Elenco {
  items: ListinoCompleto[];
}

/**
 * 💶⭐ **La stessa schermata per due listini** — voce 19, tappa 19a (24/09/2026).
 *
 * 🗣️ *«questa sezione deve essere fatta uguale a quella del listino prezzi dei
 * giocatori»*. ⇒ «Listino prezzi maestri» è **questa** schermata con
 * `per="maestri"`, non una copia: listini con le loro date, la griglia, la barra,
 * Duplica. Cambia quello che c'è dentro — **una tabella sola**, il campo
 * all'ora — e le frasi che lo dicono.
 */
export function Listini({ per = 'giocatori' }: { per?: Destinatari }): React.JSX.Element {
  const maestri = per === 'maestri';
  const chiave = [CHIAVE, per];
  const modificabile = usaModifica('manage_settings');
  const qc = useQueryClient();
  const elenco = useQuery({
    queryKey: chiave,
    queryFn: () => chiama<Elenco>('GET', `/settings/price-lists?destinatari=${per}`),
  });
  const orarioQuery = useQuery({
    queryKey: ['orario-circolo'],
    queryFn: () => chiama<OrarioCircolo>('GET', '/settings/club-hours'),
  });

  /**
   * 🤖 Si arriva qui da una segnalazione dell'assistente digitale («Metti il
   * prezzo che manca», 26/09/2026): `?listino=…&tabella=…&giorno=6&ora=18:00`
   * apre il listino e la tabella giusti, con la casella vuota scelta e segnata.
   */
  const [parametri] = useSearchParams();
  const daSegnalazione = (() => {
    const g = Number(parametri.get('giorno'));
    const ora = parametri.get('ora');
    return parametri.get('tabella') && ora && Number.isInteger(g) && g >= 0 && g <= 6
      ? { tabella: parametri.get('tabella')!, weekday: g, time: ora }
      : null;
  })();
  const [scelto, setScelto] = useState<string | null>(parametri.get('listino'));
  const [tabellaScelta, setTabellaScelta] = useState<string | null>(parametri.get('tabella'));
  /** Il listino da cui il prossimo nascerà copiato, o `null` se nasce vuoto. */
  const [duplicaDa, setDuplicaDa] = useState<ListinoCompleto | null>(null);
  /**
   * ⭐ Il modulo del listino nuovo sta CHIUSO, come «+ Tabella».
   * 🗣️ Sua, 17/09/2026: *«anche questa parte del listino puoi ottimizzarla e
   * ridurla in altezza»*. ⚖️ Un modulo sempre aperto in fondo alla pagina
   * occupa lo spazio di dieci righe di prezzi per un gesto che si fa due volte
   * l'anno; chiuso è una linguetta, e si apre quando serve davvero.
   */
  const [apriNuovo, setApriNuovo] = useState(false);
  /**
   * ⭐ La pila delle mosse: ogni scrittura si porta dietro i valori di PRIMA.
   *
   * ⚠️ Non è un lusso. Un trascinamento sbagliato tocca trenta caselle in un
   * colpo, e senza un passo indietro l'unico rimedio sarebbe riscriverle a
   * memoria — cioè indovinare quali erano vuote e quali no.
   */
  const [mosse, setMosse] = useState<{ tableId: string; celle: DaScrivere[] }[]>([]);

  const items = elenco.data?.items ?? [];
  const orario = orarioQuery.data ?? ORARIO_CIRCOLO_PREDEFINITO;
  const ore = oreDellaGriglia(orario.opensAt, orario.closesAt);
  const inVigore = listinoPerData(items, oggi());
  const listino = items.find((l) => l.id === scelto) ?? inVigore ?? items[0] ?? null;
  const tabella = listino?.tabelle.find((t) => t.id === tabellaScelta) ?? listino?.tabelle[0] ?? null;

  /**
   * ⭐⭐ Le caselle si scrivono a gruppi, e lo schermo si aggiorna PRIMA della
   * risposta.
   *
   * ⚖️ Un foglio che aspetta il server a ogni casella non è un foglio: il gesto
   * di trascinare vive sulla reazione immediata. Se la scrittura fallisce si
   * rimette tutto com'era e si dice — ed è il motivo per cui la fotografia di
   * prima si tiene da parte invece di rileggere dal server.
   */
  const scrivi = useMutation({
    meta: { fatto: 'prezzi salvati' },
    mutationFn: (p: { tableId: string; celle: DaScrivere[] }) =>
      /* ⚠️ Il foglio dice «valore» perché non sa cosa porta; la rotta dice
         «priceCents» perché lo sa benissimo. La traduzione sta QUI, su una
         riga sola: è il confine, e un confine si attraversa in un punto solo. */
      chiama('PUT', `/settings/price-tables/${p.tableId}/cells`, {
        celle: p.celle.map((c) => ({ weekday: c.weekday, time: c.time, priceCents: c.valore })),
      }),
    onMutate: async (p) => {
      await qc.cancelQueries({ queryKey: chiave });
      const prima = qc.getQueryData<Elenco>(chiave);
      qc.setQueryData<Elenco>(chiave, (vecchio) => applica(vecchio, p.tableId, p.celle));
      return { prima };
    },
    onError: (_e, _p, ctx) => {
      if (ctx?.prima) qc.setQueryData(chiave, ctx.prima);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: [CHIAVE] }),
  });

  function scriviCelle(celle: DaScrivere[]): void {
    if (tabella === null) return;
    // 📸 I valori di PRIMA, per il passo indietro: una casella che non c'era
    //    torna a non esserci (`null`), non a zero.
    const adesso = new Map(tabella.celle.map((c) => [`${c.weekday}|${c.time}`, c.priceCents]));
    const prima = celle.map((c) => ({
      weekday: c.weekday,
      time: c.time,
      valore: adesso.get(`${c.weekday}|${c.time}`) ?? null,
    }));
    setMosse((p) => [...p.slice(-19), { tableId: tabella.id, celle: prima }]);
    scrivi.mutate({ tableId: tabella.id, celle });
  }

  function annulla(): void {
    const ultima = mosse.at(-1);
    if (ultima === undefined) return;
    setMosse((p) => p.slice(0, -1));
    scrivi.mutate(ultima);
  }

  if (elenco.isLoading) return <Attesa cosa="i listini" />;
  if (elenco.error) return <Guasto errore={elenco.error} />;

  /*
   * 📏 Si contano le caselle CHE SI VEDONO, non tutte quelle che esistono.
   * ⚠️ Visto a schermo, non rileggendo il codice: riempiendo la tabella nuova
   * per l'intera giornata (48 mezz'ore) mentre la griglia ne mostra 32 — quelle
   * del circolo — il conto diceva «336 con un prezzo» e «-112 vuote». Il numero
   * negativo era il sintomo; il difetto era che due conti guardavano due
   * insiemi diversi chiamandoli con lo stesso nome.
   * 📌 *Un totale e un dettaglio devono nascere dallo stesso elenco, o prima o
   *    poi raccontano due storie.*
   */
  const dentroLOrario = new Set(ore);
  const piene = tabella?.celle.filter((c) => dentroLOrario.has(c.time)).length ?? 0;
  const vuote = ore.length * 7 - piene;

  return (
    <section className="pagina-listini">
      {/*
        📏 Due righe di spiegazione invece di quattro, e dette in una volta.
        🗣️ Sua, 17/09/2026: *«ci sono diversi spazi vuoti… puoi ottimizzare per
        salire un po' più in alto la tabella»*. ⚖️ Quello che NON si accorcia è
        la frase su «vuota ≠ zero»: è la distinzione che la segreteria deve
        avere davanti mentre scrive, non una nota di cortesia.
      */}
      <header className="testata">
        <h2>{maestri ? 'Listino prezzi maestri' : 'Listino prezzi giocatori'}</h2>
        {maestri ? (
          <p className="tenue piccolo">
            Quanto costa al maestro <strong>un’ora di campo</strong>, per giorno e per mezz’ora. Il circolo la
            trattiene dall’incasso delle sue lezioni: il maestro non versa niente. <strong>Vuota</strong> vuol
            dire «prezzo non deciso», lo <strong>zero</strong> vuol dire gratis.
          </p>
        ) : (
          <p className="tenue piccolo">
            Una tabella per tipologia e durata, una casella per ogni mezz'ora: si scrive una cifra e la si
            trascina. <strong>Vuota</strong> vuol dire «prezzo non deciso», lo <strong>zero</strong> vuol dire
            gratis.
          </p>
        )}
        <SoloLettura mostra={modificabile} />
      </header>

      {items.length === 0 ? (
        <Vuoto
          titolo="Nessun listino"
          perché={
            maestri
              ? 'Finché non ce n’è uno, le lezioni restano fuori dal conto coi maestri. Qui sotto nasce il primo.'
              : "Finché non ce n'è uno, i prezzi li danno ancora le righe di prima. Qui sotto nasce il primo."
          }
        />
      ) : (
        <div className="tab-listini" role="tablist" aria-label="I listini">
          {items.map((l) => (
            <button
              key={l.id}
              type="button"
              role="tab"
              aria-selected={l.id === listino?.id}
              className={l.id === listino?.id ? 'scelto' : ''}
              onClick={() => {
                setScelto(l.id);
                setTabellaScelta(null);
              }}
            >
              {l.title}
              <span className="tenue piccolo">{l.id === inVigore?.id ? 'in vigore oggi' : periodo(l)}</span>
            </button>
          ))}
          {modificabile && !apriNuovo && duplicaDa === null && (
            <button type="button" className="aggiungi-tabella" onClick={() => setApriNuovo(true)}>
              + Listino
            </button>
          )}
        </div>
      )}

      {/* ⚠️ Il modulo sta QUI, sotto le sue linguette, e non più in fondo alla
          pagina: è dove si guarda quando si sceglie un listino, ed è dove
          «Duplica» lo fa comparire senza dover inseguire lo scorrimento. */}
      {modificabile && (apriNuovo || duplicaDa !== null || items.length === 0) && (
        <NuovoListino
          /* 🔑 La chiave RIFÀ il modulo quando cambia il listino da copiare:
             è il modo di riempirne i campi senza un `useEffect` che insegue lo
             stato — e senza il rischio di sovrascrivere ciò che lui stava
             scrivendo. */
          key={duplicaDa?.id ?? 'vuoto'}
          per={per}
          quanti={items.length}
          duplicaDa={duplicaDa}
          lasciaStare={() => {
            setApriNuovo(false);
            setDuplicaDa(null);
          }}
          creato={(id) => {
            setApriNuovo(false);
            setDuplicaDa(null);
            setScelto(id);
            setTabellaScelta(null);
          }}
        />
      )}

      {listino !== null && (
        <>
          <Testata
            listino={listino}
            modificabile={modificabile}
            inVigore={listino.id === inVigore?.id}
            duplica={() => setDuplicaDa(listino)}
          />

          {/* 💶 Il listino dei maestri ha una tabella sola, il campo: le
              linguette delle tabelle direbbero una scelta che non c'è. */}
          {!maestri && (
            <div className="tab-tabelle" role="tablist" aria-label="Le tabelle del listino">
              {listino.tabelle.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={t.id === tabella?.id}
                  className={t.id === tabella?.id ? 'scelto' : ''}
                  onClick={() => setTabellaScelta(t.id)}
                >
                  {t.bookingType} · {t.durationMinutes}′
                  <span className="tenue piccolo">{t.perPlayer ? 'a testa' : 'il campo'}</span>
                </button>
              ))}
              {modificabile && <NuovaTabella listino={listino} ore={ore} />}
            </div>
          )}
          {/* 🆕 19c: le tabelle «lezione» e «corso» non ci sono più — decisione
              sua. Si dice dove sono andati quei prezzi, o chi cerca la linguetta
              di sempre crede che sia sparita per sbaglio. */}
          {!maestri && (
            <div className="via-tabelle">
              <p className="avvertenza">
                Le tabelle <strong>«lezione»</strong> e <strong>«corso»</strong> non ci sono più: il prezzo di
                lezioni, allenamenti e corsi lo dà il <strong>Listino prezzi lezioni</strong>, uguale a ogni
                ora e ogni giorno.
              </p>
              <Link className="bottone-bordato" to="/impostazioni/listino-lezioni">
                Apri il Listino prezzi lezioni
              </Link>
            </div>
          )}

          {tabella === null ? (
            <Vuoto
              titolo="Nessuna tabella"
              perché="Una tabella è una tipologia e una durata: «partita · 90 minuti». Se ne crea una qui sopra."
            />
          ) : (
            <>
              {/* ⭐ **COSA C'È DENTRO LE CASELLE, detto sopra la griglia**:
                  sul campo dei maestri la cella è un'ora, non una lezione.
                  📌 *Un numero fuori scala non si difende da sé: o la schermata
                     dice perché è così, o qualcuno lo raddrizza.* */}
              {tabella.bookingType === 'campo' && (
                <p className="intestazione-tabella">
                  In ogni casella quanto costa <strong>un’ora di campo</strong> che comincia in quella
                  mezz’ora. Una lezione paga <strong>ogni sua mezz’ora</strong> al prezzo della sua casella.
                </p>
              )}
              {daSegnalazione && daSegnalazione.tabella === tabella.id && (
                <p className="avvertenza da-segnalazione">
                  🤖 Dalla segnalazione dell’assistente digitale: la casella di{' '}
                  <strong>
                    {GIORNI[daSegnalazione.weekday]!.toLowerCase()} alle {daSegnalazione.time}
                  </strong>{' '}
                  è quella segnata, ed è già scelta. Scrivi il prezzo qui sotto e premi «Scrivi nella
                  casella»: da quel momento il bot offre di nuovo quell’ora.
                </p>
              )}
              <Foglio
                key={tabella.id}
                evidenzia={daSegnalazione && daSegnalazione.tabella === tabella.id ? daSegnalazione : null}
                ore={ore}
                /* Il foglio dice «valore»; il listino, di là, dice «priceCents».
                   La traduzione sta qui come sta nella rotta: in un punto solo. */
                celle={tabella.celle.map((c) => ({
                  weekday: c.weekday,
                  time: c.time,
                  valore: c.priceCents,
                }))}
                misura={tabella.bookingType === 'campo' ? prezzoDelCampo() : prezzo(tabella.perPlayer)}
                modificabile={modificabile}
                scrivi={scriviCelle}
                annulla={annulla}
                quanteMosse={mosse.length}
              />
              <Guasto errore={scrivi.error} />
              <p className={vuote > 0 ? 'guasto' : 'tenue piccolo'}>
                {vuote === 0 ? (
                  <>Tutta la settimana ha un prezzo.</>
                ) : (
                  <>
                    <strong>
                      {vuote} {vuote === 1 ? 'casella vuota' : 'caselle vuote'} su {ore.length * 7}
                    </strong>{' '}
                    {maestri
                      ? '— una lezione che ci passa resta fuori dal conto coi maestri.'
                      : '— lì la scheda si apre senza importo, e lo scrive la segreteria.'}
                  </>
                )}
                {piene > 0 && ` · ${piene} con un prezzo · ${riepilogo(tabella)}`}
              </p>
            </>
          )}
        </>
      )}

      {/*
        ⚠️ La strada di prima resta raggiungibile finché serve, e si dice perché:
        senza nessun listino i prezzi li danno ancora quelle righe. Il giorno in
        cui questo foglio è pieno, quella pagina non risponde più a nessuno.
      */}
      {maestri ? (
        <p className="tenue piccolo">
          {inVigore === null
            ? 'Nessun listino copre oggi: le lezioni di oggi restano fuori dal conto coi maestri.'
            : `Oggi il campo lo dà «${inVigore.title}».`}
        </p>
      ) : (
        <p className="tenue piccolo">
          {inVigore === null
            ? 'Nessun listino copre oggi: i prezzi li danno ancora '
            : `Oggi i prezzi li dà «${inVigore.title}». Restano consultabili `}
          <Link to="/impostazioni/listino-righe">le righe di prima</Link>.
        </p>
      )}
    </section>
  );
}

function periodo(l: ListinoCompleto): string {
  return `dal ${inItaliano(l.validFrom)}${l.validTo === null ? '' : ` al ${inItaliano(l.validTo)}`}`;
}

/** I prezzi distinti di una tabella, dal più basso al più alto. */
function riepilogo(t: TabellaPrezzi): string {
  const prezzi = [...new Set(t.celle.map((c) => c.priceCents))].sort((a, b) => a - b);
  const scritti = prezzi.slice(0, 6).map((p) => (p === 0 ? 'gratis' : euro(p)));
  const come = t.bookingType === 'campo' ? 'l’ora' : t.perPlayer ? 'a testa' : 'il campo';
  return `${scritti.join(' · ')}${prezzi.length > 6 ? ' · …' : ''} ${come}`;
}

/** Applica le caselle scritte alla fotografia in memoria, senza aspettare il server. */
function applica(vecchio: Elenco | undefined, tableId: string, celle: DaScrivere[]): Elenco | undefined {
  if (vecchio === undefined) return vecchio;
  return {
    items: vecchio.items.map((l) => ({
      ...l,
      tabelle: l.tabelle.map((t) => {
        if (t.id !== tableId) return t;
        const mappa = new Map(t.celle.map((c) => [`${c.weekday}|${c.time}`, c]));
        for (const c of celle) {
          const k = `${c.weekday}|${c.time}`;
          // ⛔ `null` TOGLIE la casella: vuoto è l'assenza, non uno zero.
          if (c.valore === null) mappa.delete(k);
          else mappa.set(k, { weekday: c.weekday, time: c.time, priceCents: c.valore });
        }
        return { ...t, celle: [...mappa.values()] };
      }),
    })),
  };
}

// ── La testata di un listino: titolo, dal, al ───────────────────────────────

function Testata({
  listino,
  modificabile,
  inVigore,
  duplica,
}: {
  listino: ListinoCompleto;
  modificabile: boolean;
  inVigore: boolean;
  duplica(): void;
}): React.JSX.Element {
  const qc = useQueryClient();
  const rileggi = (): void => void qc.invalidateQueries({ queryKey: [CHIAVE] });
  const [apri, setApri] = useState(false);
  const [titolo, setTitolo] = useState(listino.title);
  const [dal, setDal] = useState(listino.validFrom);
  const [al, setAl] = useState(listino.validTo ?? '');
  const [visto, setVisto] = useState(listino.id);
  if (listino.id !== visto) {
    setVisto(listino.id);
    setApri(false);
    setTitolo(listino.title);
    setDal(listino.validFrom);
    setAl(listino.validTo ?? '');
  }

  const salva = useMutation({
    meta: { fatto: 'listino salvato' },
    mutationFn: () =>
      chiama('PATCH', `/settings/price-lists/${listino.id}`, {
        title: titolo,
        validFrom: dal,
        validTo: al === '' ? null : al,
      }),
    onSuccess: () => {
      setApri(false);
      rileggi();
    },
  });
  const elimina = useMutation({
    meta: { fatto: 'listino eliminato' },
    mutationFn: () => chiama('DELETE', `/settings/price-lists/${listino.id}`),
    onSuccess: rileggi,
  });

  const cambiato = titolo !== listino.title || dal !== listino.validFrom || al !== (listino.validTo ?? '');

  /*
   * ⭐⭐ CHIUSA È UNA RIGA, e si apre solo per cambiare qualcosa.
   *
   * 🗣️ Sua, 17/09/2026: *«ci sono diversi spazi vuoti… per salire un po' più in
   * alto la tabella»*. 📏 Misurata prima di toccarla: questa testata si
   * prendeva **104 px** — tre caselle e una frase per dei dati che, dopo il
   * primo giorno, non si toccano più. Il foglio invece si guarda ogni volta.
   * ⚖️ Niente sparisce: titolo, periodo e «in vigore oggi» restano scritti
   * sulla riga; quello che si chiude sono le CASELLE per riscriverli.
   * 📌 *In una schermata di lavoro lo spazio va a ciò che si guarda spesso,
   *    non a ciò che si scrive una volta.*
   */
  if (!apri) {
    return (
      <div className="testata-listino compatta">
        <strong>{listino.title}</strong>
        <span className="tenue piccolo">
          {periodo(listino)}
          {inVigore ? ' · in vigore oggi' : ''}
          {listino.validTo === null ? ' · non finisce mai' : ''}
        </span>
        {modificabile && (
          <div className="azioni">
            <button type="button" className="piatto" onClick={() => setApri(true)}>
              Titolo e date
            </button>
            {/* ⭐ Duplicare: il gesto che rende facile cambiare DUE prezzi su
                duecento — si copia quello che c'è e si riscrive solo la banda
                che cambia. */}
            <button type="button" className="piatto" onClick={duplica}>
              Duplica
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <form
      className="testata-listino"
      onSubmit={(e) => {
        e.preventDefault();
        if (cambiato) salva.mutate();
      }}
    >
      <label className="campo">
        <span>Titolo</span>
        <input value={titolo} disabled={!modificabile} onChange={(e) => setTitolo(e.target.value)} />
      </label>
      <CampoData etichetta="Vale dal" value={dal} disabled={!modificabile} onChange={setDal} />
      <CampoData etichetta="Fino al" value={al} disabled={!modificabile} onChange={setAl} />
      {/* ⭐ Parole sue: «se non si mette data di fine non finisce mai quel listino». */}
      <span className="tenue piccolo">{al === '' ? 'Senza una fine, non finisce mai.' : ''}</span>
      {modificabile && (
        <div className="azioni">
          <button type="submit" disabled={!cambiato || salva.isPending}>
            {salva.isPending ? 'Un attimo…' : 'Salva'}
          </button>
          <button type="button" className="piatto" onClick={() => setApri(false)}>
            Lascia stare
          </button>
          <button
            type="button"
            className="piatto pericolo"
            disabled={elimina.isPending}
            onClick={() => elimina.mutate()}
          >
            Elimina il listino
          </button>
        </div>
      )}
      <Guasto errore={salva.error ?? elimina.error} />
    </form>
  );
}

// ── Un listino nuovo ────────────────────────────────────────────────────────

function NuovoListino({
  per,
  quanti,
  duplicaDa,
  lasciaStare,
  creato,
}: {
  per: Destinatari;
  quanti: number;
  /** Il listino da copiare, se si sta duplicando: tabelle e caselle comprese. */
  duplicaDa: ListinoCompleto | null;
  lasciaStare(): void;
  creato(id: string): void;
}): React.JSX.Element {
  const qc = useQueryClient();
  const mio = useRef<HTMLFormElement>(null);
  /*
   * ⭐ Titolo e date si riscrivono, e il titolo nasce con una PROPOSTA dentro.
   * 🗣️ Sua: *«logicamente duplicandolo bisogna reinserire il nome e la data»*.
   * ⚖️ Una proposta non è un valore ereditato: si vede, si cambia con un
   * gesto, e soprattutto impedisce due listini che si chiamano uguale — che
   * nelle linguette sarebbero indistinguibili.
   */
  const [titolo, setTitolo] = useState(duplicaDa === null ? '' : `Copia di ${duplicaDa.title}`);
  const [dal, setDal] = useState(oggi());
  const [al, setAl] = useState('');
  const crea = useMutation({
    meta: { fatto: 'listino creato' },
    mutationFn: () =>
      chiama<ListinoCompleto>('POST', '/settings/price-lists', {
        destinatari: per,
        title: titolo,
        validFrom: dal,
        validTo: al === '' ? null : al,
        copiaDa: duplicaDa?.id ?? null,
      }),
    onSuccess: (nato) => {
      setTitolo('');
      void qc.invalidateQueries({ queryKey: [CHIAVE] });
      creato(nato.id);
    },
  });

  /*
   * 📌 Il modulo sta in fondo alla pagina: premendo «Duplica» in cima, senza
   * questo, non succederebbe niente di visibile — e il gesto sembrerebbe
   * rotto proprio a chi l'ha appena chiesto.
   */
  useEffect(() => {
    if (duplicaDa !== null) mio.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [duplicaDa]);

  const quante = duplicaDa?.tabelle.length ?? 0;
  const caselle = duplicaDa?.tabelle.reduce((n, t) => n + t.celle.length, 0) ?? 0;

  return (
    <form
      ref={mio}
      className={duplicaDa === null ? 'riquadro nuovo-listino' : 'riquadro nuovo-listino si-duplica'}
      onSubmit={(e) => {
        e.preventDefault();
        crea.mutate();
      }}
    >
      <h3>
        {duplicaDa !== null
          ? `Una copia di «${duplicaDa.title}»`
          : quanti === 0
            ? 'Il primo listino'
            : 'Un altro listino'}
      </h3>
      <p className="tenue piccolo">
        {duplicaDa !== null ? (
          <>
            Arrivano <strong>{quante === 1 ? 'la sua tabella' : `le sue ${quante} tabelle`}</strong> con tutte
            le caselle ({caselle} con un prezzo): si cambiano solo quelle che devono cambiare.
          </>
        ) : (
          <>
            Un listino nuovo <strong>sostituisce</strong> quello di prima dal giorno in cui comincia. Senza
            una data di fine, non finisce mai.
            {per === 'maestri' && ' Nasce con la tabella del campo, vuota: si riempie dalla griglia.'}
          </>
        )}
      </p>
      <div className="riga-campi">
        <Campo
          etichetta="Titolo"
          required
          placeholder="Estate 2027"
          value={titolo}
          onChange={(e) => setTitolo(e.target.value)}
        />
        <CampoData etichetta="Vale dal" value={dal} onChange={setDal} />
        <CampoData etichetta="Fino al" value={al} onChange={setAl} />
      </div>
      <Guasto errore={crea.error} />
      <div className="azioni">
        <button type="submit" disabled={crea.isPending || titolo.trim() === ''}>
          {crea.isPending ? 'Un attimo…' : duplicaDa !== null ? 'Crea la copia' : 'Crea il listino'}
        </button>
        {/* ⚠️ «Lascia stare» c'è sempre tranne quando non c'è NESSUN listino:
            là chiudere lascerebbe una pagina senza niente su cui lavorare e
            senza un modo evidente di ricominciare. */}
        {quanti > 0 && (
          <button type="button" className="piatto" onClick={lasciaStare}>
            Lascia stare
          </button>
        )}
      </div>
    </form>
  );
}

// ── Una tabella nuova: tipologia, durata, e da dove copiarla ────────────────

function NuovaTabella({
  listino,
  ore,
}: {
  listino: ListinoCompleto;
  /** ⚠️ Le ore del CIRCOLO: si riempie quello che si vede, non l'intera giornata. */
  ore: string[];
}): React.JSX.Element {
  const qc = useQueryClient();
  const [aperto, setAperto] = useState(false);
  const [tipo, setTipo] = useState<TipoTabellaGiocatori>('partita');
  const [durata, setDurata] = useState('90');
  const [aTesta, setATesta] = useState(true);
  const [copiaDa, setCopiaDa] = useState('');
  const [prezzo, setPrezzo] = useState('');

  const crea = useMutation({
    meta: { fatto: 'tabella aggiunta al listino' },
    mutationFn: async () => {
      const dopo = await chiama<ListinoCompleto>('POST', `/settings/price-lists/${listino.id}/tables`, {
        bookingType: tipo,
        durationMinutes: Number(durata),
        perPlayer: aTesta,
        copiaDa: copiaDa === '' ? null : copiaDa,
      });
      /*
       * ⭐ Un prezzo di partenza riempie TUTTA la tabella in un colpo: è il
       * caso normale di un circolo — un prezzo uguale dappertutto, e poi le
       * bande della sera scritte sopra.
       */
      const cents = centesimi(prezzo);
      const nata = dopo.tabelle.find((t) => t.bookingType === tipo && t.durationMinutes === Number(durata));
      if (cents !== null && nata !== undefined && copiaDa === '') {
        await chiama('PUT', `/settings/price-tables/${nata.id}/cells`, {
          celle: [0, 1, 2, 3, 4, 5, 6].flatMap((weekday) =>
            ore.map((time) => ({ weekday, time, priceCents: cents })),
          ),
        });
      }
    },
    onSuccess: () => {
      setAperto(false);
      setPrezzo('');
      void qc.invalidateQueries({ queryKey: [CHIAVE] });
    },
  });

  if (!aperto) {
    return (
      <button type="button" className="aggiungi-tabella" onClick={() => setAperto(true)}>
        + Tabella
      </button>
    );
  }

  return (
    <form
      className="riquadro nuova-tabella"
      onSubmit={(e) => {
        e.preventDefault();
        crea.mutate();
      }}
    >
      <h3>Una tabella nuova</h3>
      <p className="tenue piccolo">
        Una tabella è <strong>una tipologia e una durata</strong>: così il prezzo resta sempre un numero che
        hai scritto tu, e il programma non ne calcola nessuno «in proporzione».
      </p>
      <div className="riga-campi">
        <label className="campo">
          <span>Tipologia</span>
          <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoTabellaGiocatori)}>
            {TIPI_TABELLA_GIOCATORI.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <Campo
          etichetta="Durata (min)"
          type="number"
          min={5}
          step={5}
          required
          value={durata}
          onChange={(e) => setDurata(e.target.value)}
        />
        <label className="campo">
          <span>Copia le caselle da</span>
          <select value={copiaDa} onChange={(e) => setCopiaDa(e.target.value)}>
            <option value="">— nasce vuota —</option>
            {listino.tabelle.map((t) => (
              <option key={t.id} value={t.id}>
                {t.bookingType} · {t.durationMinutes}′
              </option>
            ))}
          </select>
        </label>
        <Campo
          etichetta="Prezzo dappertutto (€)"
          inputMode="decimal"
          placeholder="facoltativo"
          disabled={copiaDa !== ''}
          value={prezzo}
          onChange={(e) => setPrezzo(e.target.value)}
        />
      </div>
      <label className="campo orizzontale">
        <input type="checkbox" checked={aTesta} onChange={(e) => setATesta(e.target.checked)} />
        <span>Il prezzo è a giocatore (come il listino del circolo)</span>
      </label>
      <Guasto errore={crea.error} />
      <div className="azioni">
        <button type="submit" disabled={crea.isPending}>
          {crea.isPending ? 'Un attimo…' : 'Crea la tabella'}
        </button>
        <button type="button" className="piatto" onClick={() => setAperto(false)}>
          Lascia stare
        </button>
      </div>
    </form>
  );
}
